import { useState, useEffect } from "react";
import { Pill, AlertCircle, MessageSquare, Cloud, Calendar as CalendarIcon, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";

export default function MedicationCard({ medication, anchor, onSaveAnchor, onClearAnchor, remindersEnabled }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [lastFilled, setLastFilled] = useState("");
  const [daysSupply, setDaysSupply] = useState(30);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (anchor) {
      setLastFilled(anchor.last_filled_date || "");
      setDaysSupply(anchor.days_supply || 30);
    } else {
      setLastFilled("");
      setDaysSupply(30);
    }
  }, [anchor]);

  const save = async () => {
    if (!lastFilled) return;
    setSaving(true);
    try {
      await onSaveAnchor(medication.name, lastFilled, Number(daysSupply) || 30);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const runOutLabel = anchor?.run_out_date ? new Date(anchor.run_out_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="medication-card">
      <div onClick={() => setExpanded(!expanded)} className="cursor-pointer">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center shrink-0"><Pill className="w-5 h-5 text-primary" /></div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm">{medication.name}</h3>
            <p className="text-sm text-muted-foreground">{medication.dose}</p>
            {medication.schedule && <p className="text-xs text-muted-foreground mt-1">{medication.schedule}</p>}
            {medication.source === "intakeq" && (
              <span className="inline-flex items-center gap-1 text-[10px] text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5 font-medium mt-1">
                <Cloud className="w-2.5 h-2.5" /> IntakeQ
              </span>
            )}
            {medication.source === "intakeq" && medication.source_detail === "provider_note" && medication.note_date && (
              <p className="text-[10px] text-muted-foreground mt-1" data-testid="medication-source-caption">
                Source: {medication.note_name || "Progress Note"} from{" "}
                {new Date(medication.note_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
            {medication.source === "intakeq" && medication.source_detail === "intake_form" && medication.form_name && (
              <p className="text-[10px] text-muted-foreground mt-1" data-testid="medication-source-caption">
                Source: {medication.form_name} (intake form)
              </p>
            )}
            {remindersEnabled && anchor?.run_out_date && (
              <p className="inline-flex items-center gap-1 text-[10px] text-primary bg-primary/10 border border-primary/20 rounded-full px-1.5 py-0.5 font-medium mt-1" data-testid="medication-runout-badge">
                <CalendarIcon className="w-2.5 h-2.5" /> Runs out ~{runOutLabel}
              </p>
            )}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="pt-3 border-t border-border space-y-3 animate-fade-in">
          {medication.indication && <div><p className="text-xs font-semibold text-foreground uppercase tracking-widest">What it's for</p><p className="text-sm text-muted-foreground mt-1">{medication.indication}</p></div>}
          {medication.what_to_expect && <div><p className="text-xs font-semibold text-foreground uppercase tracking-widest">What to expect</p><p className="text-sm text-muted-foreground mt-1">{medication.what_to_expect}</p></div>}
          {medication.common_side_effects && <div><p className="text-xs font-semibold text-foreground uppercase tracking-widest">Common side effects</p><p className="text-sm text-muted-foreground mt-1">{medication.common_side_effects}</p></div>}
          {medication.when_to_contact_provider && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex gap-2"><AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" /><div><p className="text-xs font-semibold text-amber-900">When to contact us</p><p className="text-sm text-amber-800 mt-1">{medication.when_to_contact_provider}</p></div></div>
            </div>
          )}

          {/* Refill reminder anchor editor */}
          {remindersEnabled && (
            <div className="bg-accent/40 border border-border rounded-lg p-3" data-testid="medication-anchor-editor">
              <p className="text-xs font-semibold text-foreground uppercase tracking-widest mb-2">Refill reminder</p>
              {!editing && anchor?.last_filled_date ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Last filled{" "}
                    <span className="text-foreground font-medium">
                      {new Date(anchor.last_filled_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    {" · "}
                    <span className="text-foreground font-medium">{anchor.days_supply}-day supply</span>
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={() => setEditing(true)} data-testid="edit-anchor-button">Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full text-destructive" onClick={() => onClearAnchor(anchor.med_key)} data-testid="clear-anchor-button">Remove</Button>
                  </div>
                </div>
              ) : !editing ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Set a fill date so we can remind you before it runs out.</p>
                  <Button size="sm" className="h-7 text-xs rounded-full" onClick={() => setEditing(true)} data-testid="set-anchor-button">Set refill reminder</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <label className="text-[11px] text-muted-foreground block mb-1">Last filled</label>
                    <Input type="date" value={lastFilled} max={new Date().toISOString().slice(0, 10)} onChange={e => setLastFilled(e.target.value)} className="h-8 text-sm" data-testid="anchor-last-filled-input" />
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground block mb-1">Days supply</label>
                    <Input type="number" min={1} max={365} value={daysSupply} onChange={e => setDaysSupply(e.target.value)} className="h-8 text-sm" data-testid="anchor-days-supply-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs rounded-full gap-1" disabled={!lastFilled || saving} onClick={save} data-testid="save-anchor-button">
                      <Check className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs rounded-full gap-1" onClick={() => setEditing(false)}>
                      <X className="w-3 h-3" /> Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Link to="/refill" className="flex-1"><Button size="sm" variant="default" className="w-full h-8 text-xs rounded-full">Request Refill</Button></Link>
            <Link to="/messages" className="flex-1"><Button size="sm" variant="outline" className="w-full h-8 text-xs gap-1.5 rounded-full"><MessageSquare className="w-3 h-3" /> Message</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
}
